const ENV_API_BASE = (import.meta.env.VITE_API_BASE || '').trim()

function shouldUseLocalCache(path) {
  const normalized = String(path || '')
  return (
    normalized.startsWith('/api/companies') ||
    normalized.startsWith('/api/dashboard')
  )
}

function shouldBypassHttpCache(path, method) {
  const normalized = String(path || '')
  const verb = String(method || 'GET').toUpperCase()
  if (verb !== 'GET') return false
  if (normalized.startsWith('/api/chat/conversations/') && normalized.includes('/messages')) return true
  return false
}

function resolveApiBase() {
  return ENV_API_BASE
}

export function buildApiUrl(path) {
  const base = resolveApiBase()
  if (!base) return path
  return `${base}${path}`
}

let perfCounter = 0

export async function apiFetch(path, options = {}) {
  const perfStart = typeof performance !== 'undefined' ? performance.now() : Date.now()
  const perfId = `${Date.now().toString(36)}-${(perfCounter++).toString(36)}`
  const perfLabel = `[PERF][API] ${String(options.method || 'GET').toUpperCase()} ${path} ${perfId}`
  let perfEnded = false
  console.time(perfLabel)
  const endPerf = (response, note) => {
    if (!perfEnded) {
      console.timeEnd(perfLabel)
      perfEnded = true
      const end = typeof performance !== 'undefined' ? performance.now() : Date.now()
      const ms = end - perfStart
      const status = response?.status ?? 'error'
      const suffix = note ? ` note=${note}` : ''
      console.log(`[PERF][API] ${String(options.method || 'GET').toUpperCase()} ${path} ms=${ms.toFixed(1)} status=${status}${suffix}`)
    }
    return response
  }

  const headers = new Headers(options.headers || {})
  let authHeaderAddedByApiFetch = false
  if (typeof window !== 'undefined') {
    const storedToken = localStorage.getItem('ondax:token')
    if (storedToken && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${storedToken}`)
      authHeaderAddedByApiFetch = true
    }
  }

  const method = String(options.method || 'GET').toUpperCase()
  const bypassHttpCache = shouldBypassHttpCache(path, method)
  if (bypassHttpCache) {
    if (!headers.has('Cache-Control')) headers.set('Cache-Control', 'no-cache')
    if (!headers.has('Pragma')) headers.set('Pragma', 'no-cache')
  }
  const hasWindowStorage =
    typeof window !== 'undefined' &&
    typeof window.localStorage !== 'undefined' &&
    typeof window.localStorage.getItem === 'function'
  const enableLocalCache = method === 'GET' && hasWindowStorage && shouldUseLocalCache(path)
  const cacheKey = enableLocalCache ? `ondax:cache:${buildApiUrl(path)}` : null
  const cacheMetaKey = cacheKey ? `${cacheKey}:meta` : null

  const parseCount = (payload) => {
    if (Array.isArray(payload)) return payload.length
    if (Array.isArray(payload?.data)) return payload.data.length
    if (Array.isArray(payload?.tickets)) return payload.tickets.length
    if (Array.isArray(payload?.orders)) return payload.orders.length
    if (Array.isArray(payload?.questions)) return payload.questions.length
    return null
  }

  const buildCachedResponse = (cached) => new Response(cached, {
    status: 200,
    headers: { 'Content-Type': 'application/json', 'X-Cache': 'HIT' },
  })

  try {
    let response = await fetch(buildApiUrl(path), {
      ...options,
      cache: bypassHttpCache ? 'no-store' : options.cache,
      headers,
      credentials: 'include',
    })

    if (response.status === 401 && method === 'GET' && !String(path).startsWith('/api/auth/')) {
      console.warn('[AUTH] 401 recebido, tentando refresh', { path })
      try {
        const refresh = await fetch(buildApiUrl('/api/auth/refresh'), { credentials: 'include' })
        console.warn('[AUTH] refresh concluído', { ok: refresh.ok, status: refresh.status })
        if (refresh.ok) {
          const retryHeaders = new Headers(headers)
          if (authHeaderAddedByApiFetch) {
            retryHeaders.delete('Authorization')
          }
          response = await fetch(buildApiUrl(path), {
            ...options,
            cache: bypassHttpCache ? 'no-store' : options.cache,
            headers: retryHeaders,
            credentials: 'include',
          })
          console.warn('[AUTH] retry após refresh', { path, status: response.status })
        }
      } catch {
        // ignore refresh errors
      }
    }

    if (method === 'GET' && response.ok && cacheKey) {
      const contentType = response.headers.get('content-type') || ''
      if (contentType.includes('application/json')) {
        let shouldWrite = true
        try {
          const metaRaw = localStorage.getItem(cacheMetaKey)
          const meta = metaRaw ? JSON.parse(metaRaw) : null
          const savedAt = Number(meta?.savedAt || 0)
          const minAgeMs = String(path).startsWith('/api/companies')
            ? 10 * 60 * 1000
            : String(path).startsWith('/api/auth/me')
              ? 30 * 1000
              : 8 * 1000
          if (savedAt && Date.now() - savedAt < minAgeMs) shouldWrite = false
        } catch {
          // ignore meta read errors
        }

        if (shouldWrite) {
          response
            .clone()
            .json()
            .then((payload) => {
              try {
                const serialized = JSON.stringify(payload)
                if (serialized.length <= 500000) {
                  localStorage.setItem(cacheKey, serialized)
                  const count = parseCount(payload)
                  localStorage.setItem(cacheMetaKey, JSON.stringify({ savedAt: Date.now(), count }))
                  console.log(`[apiFetch] Cache populado para ${path} com ${count ?? 'n/d'} registros`)
                }
              } catch {
                // ignore cache write errors
              }
            })
            .catch(() => {})
        }
      }
    }

    if (method === 'GET' && response.ok && cacheKey) {
      const contentType = response.headers.get('content-type') || ''
      if (contentType.includes('application/json')) {
        const cached = localStorage.getItem(cacheKey)
        if (cached) {
          const payload = await response.clone().json().catch(() => null)
          const count = parseCount(payload)
          const cachedPayload = JSON.parse(cached)
          const cachedCount = parseCount(cachedPayload)
          if (Number(count) === 0 && Number(cachedCount) > 0) {
            console.warn('[apiFetch] Resposta vazia, usando cache local para', path)
            return endPerf(buildCachedResponse(cached), 'cache_empty_response')
          }
        }
      }
    }

    if (!response.ok && method === 'GET' && cacheKey && typeof window !== 'undefined') {
      const cached = localStorage.getItem(cacheKey)
      if (cached) {
        if (String(path).startsWith('/api/auth/me')) {
          return endPerf(response, 'auth_me_no_cache_on_error')
        }
        console.warn('[apiFetch] Usando cache local para', path)
        return endPerf(buildCachedResponse(cached), 'cache_non_ok')
      }
    }
    return endPerf(response)
  } catch (error) {
    if (method === 'GET' && cacheKey && typeof window !== 'undefined') {
      const cached = localStorage.getItem(cacheKey)
      if (cached) {
        console.warn('[apiFetch] Falha na rede, usando cache local para', path)
        return endPerf(buildCachedResponse(cached), 'cache_network_error')
      }
    }
    endPerf(null, 'error')
    throw error
  }
}
